import { Directive, inject, effect, ViewContainerRef, EmbeddedViewRef, ElementRef } from '@angular/core';
import { PageActionsService } from './page-actions.service';

/**
 * Directive that renders page actions in the layout.
 *
 * Place this directive where you want the page actions to appear.
 * Typically used in the layout component's page header bar.
 *
 * @example
 * ```html
 * <div class="page-header">
 *   <nav class="page-header__breadcrumbs">
 *     <zag-breadcrumb [items]="breadcrumbs()" />
 *   </nav>
 *   <div class="page-header__actions" zagPageActionsOutlet></div>
 * </div>
 * ```
 */
@Directive({
  selector: '[zagPageActionsOutlet]',
})
export class PageActionsOutletDirective {
  private readonly pageActionsService = inject(PageActionsService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly elementRef = inject(ElementRef);
  private embeddedViewRef: EmbeddedViewRef<void> | null = null;

  constructor() {
    effect(() => {
      const portal = this.pageActionsService.portal();

      // Clear previous view
      if (this.embeddedViewRef) {
        this.embeddedViewRef.destroy();
        this.embeddedViewRef = null;
      }
      this.viewContainerRef.clear();

      // Create new view from portal
      if (portal) {
        this.embeddedViewRef = this.viewContainerRef.createEmbeddedView(portal.templateRef, portal.context);
      } else {
        (this.elementRef.nativeElement as HTMLElement).style.display = 'none';
      }
    });
  }
}
